import { useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { loginRequest } from '@/config/auth'
import { BrowserAuthError } from '@azure/msal-browser'
import Logo from '@/components/ui/Logo'
import { Spotlight, Users, Fingerprint, Goal } from 'lucide-react'

export function LoginPage() {
  const { instance } = useMsal()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = async (forcePopup = false) => {
    // Check if an interaction is already in progress
    const inProgress = instance.getActiveAccount() !== null && isLoading
    if (inProgress) {
      if (import.meta.env.DEV) console.log('⏭️ Login already in progress, skipping...')
      return
    }

    setIsLoading(true)
    setError(null)
    
    try {
      // Get the return URL from localStorage to preserve in MSAL state
      const returnUrl = localStorage.getItem('returnUrl')
      const loginRequestWithState = {
        ...loginRequest,
        state: returnUrl || undefined, // Pass returnUrl as state parameter
      }
      
      if (forcePopup) {
        // Use popup method when specifically requested
        await instance.loginPopup(loginRequestWithState)
      } else {
        // Use redirect method as default (more reliable)
        await instance.loginRedirect(loginRequestWithState)
      }
    } catch (error) {
      console.error('Login failed:', error)
      
      // Handle specific MSAL errors
      if (error instanceof BrowserAuthError) {
        // Handle interaction in progress error
        if (error.errorCode === 'interaction_in_progress') {
          if (import.meta.env.DEV) console.log('⏳ Authentication is already in progress')
          setError('Authentication is already in progress. Please wait...')
          // Wait a moment then try to check status
          setTimeout(() => {
            const accounts = instance.getAllAccounts()
            if (accounts.length > 0) {
              if (import.meta.env.DEV) console.log('✅ Authentication completed')
              setError(null)
              setIsLoading(false)
            }
          }, 2000)
          return
        }
        
        if (error.errorCode === 'popup_window_error' || 
            error.errorCode === 'user_cancelled' ||
            error.message.includes('Cross-Origin-Opener-Policy')) {
          setError('Popup authentication blocked. Using redirect authentication instead...')
          // Automatically try redirect if popup fails
          setTimeout(() => {
            instance.loginRedirect(loginRequest).catch(redirectError => {
              console.error('Redirect login also failed:', redirectError)
              setError('Authentication failed. Please check your network connection and try again.')
              setIsLoading(false)
            })
          }, 1000)
          return
        } else {
          setError(`Authentication error: ${error.errorMessage}`)
        }
      } else {
        setError('An unexpected error occurred during login.')
      }
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-heritage-blue via-heritage-blue to-future-blue overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-future-blue rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div>
            <Logo variant="horizontal" size="lg" type="white" theme="dark" />
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            <div>
              <h1 className="font-primary text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                (impact <span className="text-[0.75em]">&</span> growth)<sup className="text-[1em] relative -top-[0.35em] font-extrabold">+</sup> Platform
              </h1>
              <p className="text-xl text-blue-100 font-light max-w-lg">
                This was designed for you. Because it's about you!<br></br> It's your journey. Your growth. Your desires. This cool, AI-enabled and next-gen system's aim is to enhance your career experience.
              </p>
              <img 
                src="/assets/logos/Light-Logo-Impact-Growth2.png" 
                alt="My Impact & Growth Journey" 
                className="w-64 my-4"
              />
              <p className="text-xl text-blue-100 font-light max-w-lg">
                Bringing our beautifully-talented individuals together to enhance the powerful impact each team member has on Africa, our students and each other.
              </p>
            </div>

            {/* Feature highlights */}
            <div className="grid grid-cols-2 gap-6 max-w-2xl">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-white/40 via-white/20 to-white/5 backdrop-blur-md rounded-xl flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.3)] border border-white/30 ring-1 ring-white/10" style={{ transform: 'perspective(400px) rotateY(-20deg) rotateX(20deg)' }}>
                  <Goal className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Objective Alignment & Progress</h3>
                  <p className="text-xs text-blue-100 mt-1">set objectives & track outcomes</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-white/40 via-white/20 to-white/5 backdrop-blur-md rounded-xl flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.3)] border border-white/30 ring-1 ring-white/10" style={{ transform: 'perspective(400px) rotateY(-20deg) rotateX(20deg)' }}>
                  <Spotlight className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Impact Portfolio</h3>
                  <p className="text-xs text-blue-100 mt-1">showcase outcomes & impact</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-white/40 via-white/20 to-white/5 backdrop-blur-md rounded-xl flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.3)] border border-white/30 ring-1 ring-white/10" style={{ transform: 'perspective(400px) rotateY(-20deg) rotateX(20deg)' }}>
                  <Users className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Reflections & Feedback</h3>
                  <p className="text-xs text-blue-100 mt-1">meaningfully engage via quarterly 1:1 <br></br>check-in dialogues as well as ad-hoc feedback requests of anyone at anytime</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-white/40 via-white/20 to-white/5 backdrop-blur-md rounded-xl flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.3)] border border-white/30 ring-1 ring-white/10" style={{ transform: 'perspective(400px) rotateY(-20deg) rotateX(20deg)' }}>
                  <Fingerprint className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Personalised Growth</h3>
                  <p className="text-xs text-blue-100 mt-1">curate a growth journey of mindsets, skills and behaviours unique to role and personal desires -- there could be as many journeys as we have people!</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-sm text-blue-100">
            <p>© 2026 Eduvos. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-20 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo variant="horizontal" size="lg" />
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-primary font-bold text-gray-900">
                Welcome Back
              </h2>
              <p className="text-gray-600">
                Sign in to access your impact & growth journey
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Login button */}
            <div className="space-y-4">
              <button
                onClick={() => handleLogin(false)}
                disabled={isLoading}
                className="group relative w-full flex items-center justify-center py-4 px-6 border border-transparent text-base font-medium rounded-lg text-white bg-heritage-blue hover:bg-heritage-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-heritage-blue transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                </svg>
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign in with Microsoft'
                )}
              </button>

              <p className="text-xs text-center text-gray-500">
                Use your Eduvos Microsoft account credentials
              </p>
            </div>

            {/* Additional info */}
            <div className="pt-6 border-t border-gray-200">
              <p className="text-xs text-center text-gray-600">
                Secure authentication powered by Microsoft Azure AD
              </p>
            </div>
          </div>

          {/* Help text */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Need help signing in?{' '}
              <a href="mailto:support@eduvos.com" className="text-heritage-blue hover:text-future-blue font-medium transition-colors">
                Contact IT Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
